package ro.horatiu_udrea.resursedeblocat

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import coil3.ImageLoader
import coil3.compose.setSingletonImageLoaderFactory
import coil3.memory.MemoryCache
import coil3.network.ktor3.KtorNetworkFetcherFactory
import coil3.request.crossfade
import coil3.util.DebugLogger
import io.ktor.client.*
import io.ktor.client.plugins.*

@Composable
fun App() {
    setSingletonImageLoaderFactory { context ->
        ImageLoader.Builder(context)
            .components {
                add(KtorNetworkFetcherFactory(HttpClient {
                    install(HttpRequestRetry) {
                        retryOnExceptionOrServerErrors(maxRetries = 3)
                        exponentialDelay()
                    }
                }))
            }
            .memoryCache {
                MemoryCache.Builder()
                    .maxSizePercent(context, 0.25)
                    .build()
            }
            .crossfade(true)
            .logger(DebugLogger())
            .build()
    }

    AppTheme {
        val platform = remember { getPlatform() }
        var currentUser by remember { mutableStateOf<User?>(null) }
        var isSearching by remember { mutableStateOf(false) }
        var isLoading by remember { mutableStateOf(false) }
        var isInitializing by remember { mutableStateOf(true) }
        var loginError by remember { mutableStateOf<String?>(null) }
        val resources = remember { mutableStateListOf<ResourceDetails>() }
        var selectedResourceIndex by remember { mutableStateOf<Int?>(null) }
        
        var currentHomeTab by remember { mutableStateOf(HomeTab.RESOURCES) }
        var activeTrackingMonthYear by remember { mutableStateOf<MonthYear?>(null) }
        var activeTrackingItem by remember { mutableStateOf<MonthlyTracking?>(null) }

        val trackingViewModel = remember(currentUser) { 
            currentUser?.let { TrackingViewModel(it.id) }
        }

        val adminViewModel = remember(currentUser) {
            if (currentUser?.isAdmin == true) AdminViewModel() else null
        }
        
        LaunchedEffect(currentUser) {
            val user = currentUser
            if (user != null) {
                isLoading = true
                getResources(
                    userId = user.id,
                    onSuccess = { fetchedResources ->
                        resources.clear()
                        resources.addAll(fetchedResources.sortedByDescending { it.addedAt })
                        isLoading = false
                    },
                    onFailure = { error ->
                        println("Error fetching resources: $error")
                        isLoading = false
                    }
                )
            } else {
                resources.clear()
            }
        }

        LaunchedEffect(Unit) {
            onAuthStateChanged { user ->
                currentUser = user
                isInitializing = false
            }
        }

        val selectedResource = selectedResourceIndex?.let { resources.getOrNull(it) }

        val currentScreen = when {
            isInitializing -> Screen.Loading
            currentUser == null -> Screen.Welcome
            isSearching -> Screen.Search
            selectedResourceIndex != null -> Screen.Detail
            activeTrackingMonthYear != null || activeTrackingItem != null -> Screen.TrackQuestionnaire
            else -> Screen.List
        }

        Crossfade(targetState = currentScreen) { screen ->
            when (screen) {
                Screen.Loading -> {
                    // Putem adăuga un splash screen sau un loader simplu aici
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }
                Screen.Welcome -> {
                    WelcomeScreen(
                        onLoginClick = {
                            isLoading = true
                            loginError = null
                            executeGoogleLogin(
                                onSuccess = { user ->
                                    currentUser = user
                                    isLoading = false
                                },
                                onFailure = { error ->
                                    loginError = error
                                    isLoading = false
                                }
                            )
                        },
                        isLoading = isLoading,
                        error = loginError
                    )
                }
                Screen.Search -> {
                    SearchResourceScreen(
                        onBackToHome = {
                            isSearching = false
                        },
                        onResourceFound = { newResource ->
                            val index = resources.indexOfFirst { it.id == newResource.id }
                            if (index != -1) {
                                resources[index] = newResource
                                println("Resource updated in list from search screen: ${newResource.id}")
                            } else {
                                resources.add(0, newResource)
                                println("New resource added to list from search screen: ${newResource.id}")
                            }
                        },
                        currentUser = currentUser,
                        existingResources = resources
                    )
                }
                Screen.Detail -> {
                    if (selectedResourceIndex != null) {
                        val resource = resources.getOrNull(selectedResourceIndex!!)
                        if (resource != null) {
                            key(resource.id, resource.state) {
                                ResourceDetailPage(
                                    details = resource,
                                    onBack = { selectedResourceIndex = null },
                                    showOptions = true,
                                    onStateChange = { newState ->
                                        val user = currentUser
                                        if (user != null) {
                                            updateResourceState(
                                                userId = user.id,
                                                resourceId = resource.id,
                                                newState = newState,
                                                onSuccess = {
                                                    val updated = resource.copy(state = newState)
                                                    resources[selectedResourceIndex!!] = updated
                                                    // Force UI to see the update
                                                    println("Resource state updated locally to: ${updated.state} at index ${selectedResourceIndex!!}")
                                                    println("Current resource state after update in resources list: ${resources[selectedResourceIndex!!].state}")
                                                },
                                                onFailure = { error ->
                                                    println("Error updating state: $error")
                                                }
                                            )
                                        }
                                    },
                                    onDelete = {
                                        val user = currentUser
                                        if (user != null) {
                                            deleteResource(
                                                userId = user.id,
                                                resourceId = resource.id,
                                                onSuccess = {
                                                    resources.removeAt(selectedResourceIndex!!)
                                                    selectedResourceIndex = null
                                                },
                                                onFailure = { error ->
                                                    println("Error deleting resource: $error")
                                                }
                                            )
                                        }
                                    }
                                )
                            }
                        }
                    }
                }
                Screen.List -> {
                    Column(modifier = Modifier.fillMaxSize()) {
                        HomeTabSelector(
                            selectedTab = currentHomeTab,
                            onTabSelected = { currentHomeTab = it },
                            showAdmin = currentUser?.isAdmin ?: false
                        )
                        when (currentHomeTab) {
                            HomeTab.RESOURCES -> {
                                ResourceListScreen(
                                    resources = resources,
                                    isLoading = isLoading,
                                    onFindNewResource = {
                                        isSearching = true
                                    },
                                    onResourceClick = { resource ->
                                        selectedResourceIndex = resources.indexOf(resource)
                                    },
                                    onLogout = {
                                        executeLogout()
                                        currentUser = null
                                    }
                                )
                            }
                            HomeTab.TRACKING -> {
                                trackingViewModel?.let { vm ->
                                    TrackingScreen(
                                        viewModel = vm,
                                        onAddTracking = { activeTrackingMonthYear = it },
                                        onTrackingClick = { activeTrackingItem = it },
                                        onLogout = {
                                            executeLogout()
                                            currentUser = null
                                        }
                                    )
                                }
                            }
                            HomeTab.ADMIN -> {
                                adminViewModel?.let { vm ->
                                    AdminScreen(
                                        viewModel = vm,
                                        onLogout = {
                                            executeLogout()
                                            currentUser = null
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
                Screen.TrackQuestionnaire -> {
                    currentUser?.let { user ->
                        val currentMonthYear = activeTrackingMonthYear
                        val currentItem = activeTrackingItem
                        
                        if (currentMonthYear != null || currentItem != null) {
                            val month = currentMonthYear?.month ?: currentItem?.month ?: 0
                            val year = currentMonthYear?.year ?: currentItem?.year ?: 0
                            
                            if (month != 0 && year != 0) {
                                val vm = remember(user.id, month, year) {
                                    TrackQuestionnaireViewModel(user.id, month, year, currentItem)
                                }
                                TrackQuestionnaireScreen(
                                    viewModel = vm,
                                    onBack = {
                                        activeTrackingMonthYear = null
                                        activeTrackingItem = null
                                    },
                                    onFinished = {
                                        activeTrackingMonthYear = null
                                        activeTrackingItem = null
                                        trackingViewModel?.loadTrackings()
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

private sealed class Screen {
    data object Loading : Screen()
    data object Welcome : Screen()
    data object List : Screen()
    data object Search : Screen()
    data object Detail : Screen()
    data object TrackQuestionnaire : Screen()
}